import * as React from 'react';

import { OrganizationFeature } from '@gitbook/api-client';

import { useCurrentOrganization, useGetUpgradeAction } from '../../state';
import { type AgentUpgradeOpenReason, useChangeRequestAgentLimit } from '../Agent';
import type { AgentChatMessageSource, AgentChatProps } from './AgentChat';
import { AgentLimitReachedBanner, AgentLimitReachedCard } from './AgentLimitReached';

/**
 * Hold a free, non-trial user to their weekly agent message allowance, and offer the upgrade that
 * lifts it.
 *
 * Returns the {@link AgentChatProps} that enforce and surface it, so every surface hosting the chat
 * gets the same behaviour: the send is intercepted rather than sent, a banner warns on an empty
 * conversation, and a card explains it in the message flow once a send has been attempted.
 */
export function useAgentMessageLimit(input: {
    /** Whether the conversation already holds messages, which picks the banner or the card. */
    hasMessages: boolean;
    onTrackReachLimit: (input: {
        messageSource: AgentChatMessageSource;
        messagesSentLast7Days: number;
        limit: number;
    }) => void;
    onTrackOpenUpgrade: (reason: AgentUpgradeOpenReason) => void;
}): Pick<
    AgentChatProps,
    'isSubmissionBlocked' | 'onSubmissionBlocked' | 'bannerAboveInput' | 'messageFlowFooter'
> {
    const organization = useCurrentOrganization();
    const { hasMessages, onTrackReachLimit, onTrackOpenUpgrade } = input;
    const upgrade = useGetUpgradeAction({
        organization,
        featureKey: OrganizationFeature.AiWriting,
    });
    const { isLimitReached, weeklyTotal, limit } = useChangeRequestAgentLimit();
    // The attempt is remembered against the usage it was made at, rather than reset from an effect
    // when the limit lifts. A week rolling over or a plan changing moves the count, which retires
    // the attempt by derivation — and a blocked send never moves it, so the card stays put while
    // the user is still looking at it.
    const [attemptedAtWeeklyTotal, setAttemptedAtWeeklyTotal] = React.useState<number | null>(null);
    const hasAttemptedBlockedSend = attemptedAtWeeklyTotal === weeklyTotal;

    const onSubmissionBlocked = React.useCallback(
        (blocked: { messageSource: AgentChatMessageSource; messageText: string }) => {
            setAttemptedAtWeeklyTotal(weeklyTotal);
            onTrackReachLimit({
                messageSource: blocked.messageSource,
                messagesSentLast7Days: weeklyTotal,
                limit,
            });
        },
        [limit, onTrackReachLimit, weeklyTotal]
    );

    return React.useMemo(() => {
        if (!upgrade) {
            // Nothing to offer, so nothing to block on: an organization with no upgrade path is
            // either already entitled or cannot buy its way past the limit here.
            return { isSubmissionBlocked: false };
        }

        return {
            isSubmissionBlocked: isLimitReached,
            onSubmissionBlocked,
            bannerAboveInput:
                isLimitReached && !hasMessages ? (
                    <AgentLimitReachedBanner
                        key="agent-limit"
                        upgrade={upgrade}
                        onUpgradeClick={() => onTrackOpenUpgrade('above_input_banner')}
                    />
                ) : null,
            messageFlowFooter:
                isLimitReached && hasAttemptedBlockedSend ? (
                    <AgentLimitReachedCard
                        upgrade={upgrade}
                        onUpgradeClick={() => onTrackOpenUpgrade('in_flow_card')}
                    />
                ) : null,
        };
    }, [
        hasAttemptedBlockedSend,
        hasMessages,
        isLimitReached,
        onSubmissionBlocked,
        onTrackOpenUpgrade,
        upgrade,
    ]);
}
